import { List } from "../../@types/list";
import {
  AnsweringClient,
  SearchAllUsersParams,
} from "../../clients/answeringClient";
import { describeApiError, isExpiredSession } from "../../utils/apiError";
import { IResellerSearchResult } from "../types";

export interface IDemotionImpact {
  clientsCount: number;
  activeLicensesCount: number;
}

export interface ResellersControllerDelegate {
  didGetResellers(response: List<IResellerSearchResult>): void;
  didGetDemotionImpact(impact: IDemotionImpact): void;
  didFailGetDemotionImpact(message: string): void;
  didDemoteReseller(): void;
  didFailDemoteReseller(message: string): void;
  applicationShouldLogout(): void;
  genericError(message: string): void;
}

export class ResellersController {
  constructor(
    private delegate: ResellersControllerDelegate,
    private readonly answeringClient = new AnsweringClient()
  ) {}

  public async searchResellers(
    accessToken: string,
    params: SearchAllUsersParams
  ) {
    try {
      const response = await this.answeringClient.searchResellers(
        accessToken,
        params
      );
      this.delegate.didGetResellers(response.data);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "Ocorreu um erro ao buscar os revendedores.";
      this.delegate.genericError(errorMessage);
    }
  }

  public async getDemotionImpact(
    accessToken: string,
    resellerUid: string,
    hasRetried = false
  ): Promise<void> {
    try {
      const response = await this.answeringClient.getDemotionImpact(
        accessToken,
        resellerUid
      );
      this.delegate.didGetDemotionImpact(response.data);
    } catch (error: any) {
      if (isExpiredSession(error) && !hasRetried) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        return this.getDemotionImpact(newTokens.accessToken, resellerUid, true);
      }
      this.delegate.didFailGetDemotionImpact(
        describeApiError(error, "Erro ao buscar dados do revendedor.")
      );
    }
  }

  public async demoteReseller(
    accessToken: string,
    resellerUid: string,
    hasRetried = false
  ): Promise<void> {
    try {
      await this.answeringClient.demoteReseller(accessToken, resellerUid);
      this.delegate.didDemoteReseller();
    } catch (error: any) {
      if (isExpiredSession(error) && !hasRetried) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        return this.demoteReseller(newTokens.accessToken, resellerUid, true);
      }
      this.delegate.didFailDemoteReseller(
        describeApiError(error, "Falha ao remover o cargo do revendedor.")
      );
    }
  }

  private async refreshTokens(): Promise<{
    accessToken: string;
    refreshToken: string;
  } | void> {
    const refreshToken = localStorage.getItem("RefreshToken");
    if (!refreshToken) {
      this.delegate.applicationShouldLogout();
      return;
    }
    try {
      const response = await this.answeringClient.refreshToken(refreshToken);
      const tokens: { accessToken: string; refreshToken: string } =
        response.data;
      localStorage.setItem("AccessToken", tokens.accessToken);
      localStorage.setItem("RefreshToken", tokens.refreshToken);
      return tokens;
    } catch (error: any) {
      this.delegate.applicationShouldLogout();
      return;
    }
  }
}
